import SEO from '@/components/SEO';
import usePageContent from '@/lib/usePageContent';

const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=art.kalamanch.app';

const defaultContent = {
  hero_title: 'KalaManch Mobile App',
  hero_subtitle: 'The KalaManch Android app is live for artists, collaborators, organizers, and creative teams who want faster discovery, PMB messages, profiles, projects, competitions, and AI tools on mobile.',
  android_status: 'Android app is live on Google Play.',
  ios_status: 'iOS app is planned after Android launch.',
  android_store_url: ANDROID_PLAY_STORE_URL,
  ios_store_url: '',
  features_title: 'What artists and collaborators can do',
  features_body: 'Browse verified artists, manage artist profiles, send and reply to PMB messages, discover collaborators, upload portfolio media, track requests, and use KalaManch support from mobile.',
  ai_tools_title: 'AI Studio on mobile',
  ai_tools_body: 'Phase 1 AI tools focus on low-cost creator growth: artist bio, captions, hashtags, reel scripts, event announcements, poster copy, and artist statements. AI Studio remains limited by free beta usage until pricing is launched.',
  cta_title: 'Download KalaManch on Android',
  cta_description: 'Install the Android app from Google Play. iOS will be added after the iPhone app launch.',
};

const mobileFeatureGroups = [
  {
    title: 'Discover',
    items: [
      'Browse artists and collaborators',
      'Near Me and city-based discovery',
      'Seasonal and mood discovery',
      'Bharat Talent Map',
    ],
  },
  {
    title: 'Profile',
    items: [
      'Artist profile and public portfolio',
      'Photos, videos, reels, and YouTube links',
      'Native language bio support',
      'Open for Work status',
    ],
  },
  {
    title: 'Connect',
    items: [
      'PMB private messages',
      'Collaboration requests',
      'Digital visiting card sharing',
      'Push notifications where enabled',
    ],
  },
  {
    title: 'Opportunities',
    items: [
      'Projects and work opportunities',
      'Competition browsing and submissions',
      'Live audition discovery',
      'Artist accept or decline flows',
    ],
  },
  {
    title: 'AI Studio',
    items: [
      'Artist bio and statements',
      'Captions, hashtags, and reel scripts',
      'Event planner and event artist finder',
      'Free beta usage controls',
    ],
  },
  {
    title: 'Safety',
    items: [
      'Support page and reporting',
      'Admin-reviewed competition entries',
      'Content and AI safety checks',
      'Shared website and app data',
    ],
  },
];

const StoreBadge = ({ type, href }) => {
  const isAndroid = type === 'android';
  const label = isAndroid ? 'Android app' : 'iOS app';
  const badgeSrc = isAndroid ? '/images/store/google-play-badge.svg' : '/images/store/app-store-badge.svg';
  const inner = (
    <span className="block w-full max-w-[18rem]">
      <img
        src={badgeSrc}
        alt={label}
        className="h-auto w-full rounded-2xl shadow-lg"
      />
      {!href && (
        <span className="mt-2 block text-center text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          Coming soon
        </span>
      )}
    </span>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
        {inner}
      </a>
    );
  }
  return inner;
};

export default function MobileAppPage() {
  const managed = usePageContent('mobile_app');
  const content = { ...defaultContent, ...managed };
  const androidStoreUrl = content.android_store_url || ANDROID_PLAY_STORE_URL;
  const androidStatus = /coming soon/i.test(content.android_status || '')
    ? defaultContent.android_status
    : content.android_status;

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-36">
      <SEO
        title="KalaManch Mobile App"
        description="KalaManch mobile app for artists and collaborators with profiles, PMB messages, discovery, and AI Studio tools."
        keywords="KalaManch app, artist app India, collaborator app, AI tools for artists"
      />

      <section className="mx-auto max-w-7xl px-4 pb-12 pt-10 text-center sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Mobile App</p>
          <h1 className="mx-auto mt-4 max-w-4xl text-4xl font-extrabold leading-tight text-gray-950 sm:text-6xl">{content.hero_title}</h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-650">{content.hero_subtitle}</p>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <StoreBadge type="android" href={androidStoreUrl} />
            <StoreBadge type="ios" href={content.ios_store_url} />
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-teal-100 bg-teal-50 p-5 text-center shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#0f766e]">Android</p>
              <p className="mt-2 text-sm font-semibold leading-6 text-gray-800">{androidStatus}</p>
            </div>
            <div className="rounded-3xl border border-slate-800 bg-slate-950 p-5 text-center shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-white">iOS</p>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-200">{content.ios_status}</p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Android App Features</p>
            <h2 className="mx-auto mt-3 max-w-3xl text-3xl font-extrabold text-gray-950 sm:text-4xl">
              Everything connected with the KalaManch website
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-7 text-gray-650">
              Artists, collaborators, organizers, and creative teams can use the same shared KalaManch data across website and Android app.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {mobileFeatureGroups.map((group) => (
              <article key={group.title} className="rounded-3xl border border-slate-200 bg-[#f7f9fc] p-5 shadow-sm">
                <h3 className="text-lg font-extrabold text-gray-950">{group.title}</h3>
                <div className="mt-4 grid gap-2">
                  {group.items.map((item) => (
                    <div key={item} className="flex items-start gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-semibold leading-6 text-gray-750 shadow-sm">
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-950">{content.features_title}</h2>
              <p className="mt-4 whitespace-pre-wrap text-base leading-8 text-gray-700">{content.features_body}</p>
            </article>
            <article className="rounded-3xl border border-teal-100 bg-teal-50/70 p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-950">{content.ai_tools_title}</h2>
              <p className="mt-4 whitespace-pre-wrap text-base leading-8 text-gray-700">{content.ai_tools_body}</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 text-center sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-950">{content.cta_title}</h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-gray-650">{content.cta_description}</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <StoreBadge type="android" href={androidStoreUrl} />
          <StoreBadge type="ios" href={content.ios_store_url} />
        </div>
      </section>
    </main>
  );
}
